import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';

import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';
import {
  buildOptionalPhoneNumberSchema,
  buildPhoneNumberSchema,
} from '@klareospace/pharos-lib-front-utils/phone-validation';
import type { Customer, CustomerType, GeocodingSource, PhoneNumber } from '@klareospace/pharos-lib-types';
import { CUSTOMER_TYPE, GEOCODING_SOURCE } from '@klareospace/pharos-lib-types';

import type { AddressBookEntryInput, ContactField, CustomerFormInput } from '../schemas/customer.schema';
import {
  buildAddressEntryFromCustomerAddress,
  buildEmptyAddressEntry,
  buildPayload,
  hasAddressData,
  isAddressEntryComplete,
} from '../utils/customer-form.utils';
import { useMasterContext } from '../../../master/master-context';
import { useCreateCustomer } from './use-create-customer';
import { useUpdateCustomer } from './use-update-customer';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { AddressBookEntryInput, ContactField, CustomerFormInput };

export interface UseCustomerFormOptions {
  customer: Customer | null;
  /** Called with the newly created customer on a successful create (not fired on update). */
  onCreated?: (customer: Customer) => void;
  onSuccess: () => void;
}

export interface UseCustomerFormReturn {
  form: UseFormReturn<CustomerFormInput>;
  handleContactFieldChange: <TField extends ContactField>(
    index: number,
    field: TField,
    value: AddressBookEntryInput[TField],
  ) => void;
  isPending: boolean;
  isSubmitDisabled: boolean;
  onSubmit: (data: CustomerFormInput) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

interface AddressEntrySchemaMessages {
  contactPhoneInvalid?: string;
}

type AddressEntrySchema = z.ZodObject<{
  address: z.ZodObject<{
    additionalInformations: z.ZodOptional<z.ZodString>;
    city: z.ZodUnion<[z.ZodOptional<z.ZodString>, z.ZodLiteral<''>]>;
    complement: z.ZodOptional<z.ZodString>;
    country: z.ZodUnion<[z.ZodOptional<z.ZodString>, z.ZodLiteral<''>]>;
    digicode: z.ZodOptional<z.ZodString>;
    floor: z.ZodOptional<z.ZodString>;
    hasElevator: z.ZodOptional<z.ZodBoolean>;
    houseNumber: z.ZodOptional<z.ZodString>;
    postalCode: z.ZodUnion<[z.ZodOptional<z.ZodString>, z.ZodLiteral<''>]>;
    street: z.ZodOptional<z.ZodString>;
  }>;
  contactName: z.ZodOptional<z.ZodString>;
  contactPhone: ReturnType<typeof buildOptionalPhoneNumberSchema>;
  coordinates: z.ZodOptional<
    z.ZodObject<{ latitude: z.ZodOptional<z.ZodNumber>; longitude: z.ZodOptional<z.ZodNumber> }>
  >;
  geocodingRawScore: z.ZodOptional<z.ZodNumber>;
  geocodingScore: z.ZodOptional<z.ZodNumber>;
  geocodingSource: z.ZodOptional<z.ZodEnum<Record<GeocodingSource, GeocodingSource>>>;
  isCustomerContact: z.ZodBoolean;
  isDefault: z.ZodBoolean;
  sourceIndex: z.ZodOptional<z.ZodNumber>;
}>;

function buildAddressEntrySchema(messages: AddressEntrySchemaMessages): AddressEntrySchema {
  return z.object({
    address: z.object({
      additionalInformations: z.string().optional(),
      city: z.string().optional().or(z.literal('')),
      complement: z.string().optional(),
      country: z.string().optional().or(z.literal('')),
      digicode: z.string().optional(),
      floor: z.string().optional(),
      hasElevator: z.boolean().optional(),
      houseNumber: z.string().optional(),
      postalCode: z.string().optional().or(z.literal('')),
      street: z.string().optional(),
    }),
    contactName: z.string().optional(),
    contactPhone: buildOptionalPhoneNumberSchema({ invalid: messages.contactPhoneInvalid }),
    coordinates: z
      .object({
        latitude: z.number().optional(),
        longitude: z.number().optional(),
      })
      .optional(),
    geocodingRawScore: z.number().optional(),
    geocodingScore: z.number().optional(),
    geocodingSource: z.enum(Object.values(GEOCODING_SOURCE) as [GeocodingSource, ...GeocodingSource[]]).optional(),
    isCustomerContact: z.boolean(),
    isDefault: z.boolean(),
    sourceIndex: z.number().optional(),
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCustomerForm({ customer, onCreated, onSuccess }: UseCustomerFormOptions): UseCustomerFormReturn {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('customers');
  const { siteId, spaceId } = useMasterContext();
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();

  const isEditMode = customer !== null;

  const schema = useMemo(
    () =>
      z
        .object({
          addresses: z.array(buildAddressEntrySchema({ contactPhoneInvalid: tCommon('validation.phoneInvalid') })),
          companyName: z.string().optional().or(z.literal('')),
          email: z.string().email().optional().or(z.literal('')),
          firstName: z.string().trim().min(1),
          lastName: z.string().trim().min(1),
          loyaltyNumber: z.string().optional().or(z.literal('')),
          phone: buildPhoneNumberSchema({
            invalid: tCommon('validation.phoneInvalid'),
            required: tCommon('validation.phoneRequired'),
          }),
          type: z.enum(Object.values(CUSTOMER_TYPE) as [CustomerType, ...CustomerType[]]),
        })
        .superRefine((data, ctx): void => {
          if (data.type === CUSTOMER_TYPE.COMPANY && !data.companyName?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('form.validation.companyNameRequired'),
              path: ['companyName'],
            });
          }
        })
        .superRefine((data, ctx): void => {
          data.addresses.forEach((entry, index) => {
            if (entry.isCustomerContact) return;
            if (!entry.contactName?.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('form.validation.contactNameRequired'),
                path: ['addresses', index, 'contactName'],
              });
            }
            if (!entry.contactPhone?.number?.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('form.validation.contactPhoneRequired'),
                path: ['addresses', index, 'contactPhone'],
              });
            }
          });
        })
        .superRefine((data, ctx): void => {
          // Only entries added this session (no sourceIndex) are gated: hydrated legacy entries may
          // carry stored coordinate-less addresses, and their unrelated personal-info edits must not
          // be blocked by this rule.
          data.addresses.forEach((entry, index) => {
            if (entry.sourceIndex !== undefined) return;
            if (!hasAddressData(entry) || isAddressEntryComplete(entry)) return;
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('addressBook.dialog.incomplete'),
              path: ['addresses', index, 'coordinates'],
            });
          });
        }),
    [t],
  );

  type SchemaInput = z.infer<typeof schema>;

  const form = useForm<SchemaInput>({
    defaultValues: isEditMode ? buildEditDefaultValues(customer) : buildCreateDefaultValues(),
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  // Reset form when switching between create/edit mode (customer reference changes).
  useEffect(() => {
    form.reset(customer ? buildEditDefaultValues(customer) : buildCreateDefaultValues());
    if (customer) {
      // In edit mode, trigger validation so isValid reflects the pre-filled data.
      void form.trigger();
    }
  }, [customer?.id]);

  // Re-validate every entry's contactName/contactPhone whenever any entry's isCustomerContact
  // flag changes (both directions), mirroring the single-address hook's original targeted
  // re-trigger — RHF's own onChange revalidation does not reliably re-evaluate *other* field
  // names against the current schema, so this is the only reliable way to keep formState.errors
  // accurate across the whole array.
  const watchedAddresses = useWatch({ control: form.control, name: 'addresses' });
  const contactFlagsKey = watchedAddresses?.map((entry) => entry.isCustomerContact).join('|');

  useEffect(() => {
    void form.trigger('addresses');
  }, [contactFlagsKey, form]);

  // Suppress the "required" error on each entry's contactName/contactPhone until the user has
  // actually interacted with them — same rationale as the single-address version this replaces
  // (see the original use-customer-form.ts history): revealing these fields (toggle OFF) stamps
  // them as invalid before the user ever touches them, so the displayed error is cleared until
  // dirty/touched, without affecting formState.isValid (submit stays correctly disabled).
  //
  // `dirtyFields`/`touchedFields` must be read here (in the render body, not only inside the
  // effect below) — react-hook-form's formState is a Proxy that only starts tracking a given
  // key once it has been read during render.
  const { dirtyFields, errors, touchedFields } = form.formState;

  useLayoutEffect(() => {
    const addressErrors = (errors.addresses ?? []) as ContactErrorEntry[];
    addressErrors.forEach((entryErrors, index) => {
      suppressUntouchedContactErrors(
        form.clearErrors,
        index,
        entryErrors,
        dirtyFields.addresses?.[index],
        touchedFields.addresses?.[index],
      );
    });
  }, [errors.addresses, dirtyFields.addresses, touchedFields.addresses, form]);

  /**
   * Marks an entry's contact-override field touched on every keystroke (not just on blur, RHF's
   * default) — required so the field's own "required" error stays visible even if the user
   * later clears it back to an empty string. `as never` mirrors the same dynamic-field-name
   * escape hatch used by AddressInput's `onSetValue` callback.
   */
  function handleContactFieldChange<TField extends ContactField>(
    index: number,
    field: TField,
    value: AddressBookEntryInput[TField],
  ): void {
    form.setValue(`addresses.${index}.${field}` as never, value as never, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  const isPending = isEditMode ? updateCustomer.isPending : createCustomer.isPending;

  function onSubmit(data: CustomerFormInput): void {
    if (isPending) return;
    if (isEditMode) {
      submitUpdate(customer.id, data);
    } else {
      submitCreate(data);
    }
  }

  function handleApiError(error: unknown, descriptionKeyPrefix: 'create' | 'edit'): void {
    const detail = getApiErrorDetail(error);

    function handleDuplicate(field: 'email' | 'phone', messageKey: string): void {
      form.setError(field, { message: t(`form.validation.${messageKey}`), type: 'manual' });
      toast.error(tCommon('error'), { description: t(`form.validation.${messageKey}`) });
    }

    switch (detail.errorCode) {
      case 'Conflict.DuplicateEmail':
        handleDuplicate('email', 'duplicateEmail');
        break;
      case 'Conflict.DuplicatePhone':
        handleDuplicate('phone', 'duplicatePhone');
        break;
      default:
        toast.error(tCommon('error'), { description: detail.message || t(`${descriptionKeyPrefix}.errorDescription`) });
    }
  }

  function submitCreate(data: CustomerFormInput): void {
    const displayName = `${data.firstName} ${data.lastName}`;
    createCustomer.mutateAsync(buildPayload(data, undefined, { siteId, spaceId })).then(
      (createdCustomer) => {
        toast.success(t('create.success'), { description: t('create.successDescription', { name: displayName }) });
        onCreated?.(createdCustomer);
        onSuccess();
      },
      (error: unknown) => {
        handleApiError(error, 'create');
      },
    );
  }

  function submitUpdate(customerId: string, data: CustomerFormInput): void {
    const displayName = `${data.firstName} ${data.lastName}`;
    updateCustomer.mutateAsync({ id: customerId, patch: buildPayload(data, customer ?? undefined) }).then(
      () => {
        toast.success(t('edit.success'), { description: t('edit.successDescription', { name: displayName }) });
        onSuccess();
      },
      (error: unknown) => {
        handleApiError(error, 'edit');
      },
    );
  }

  return {
    form: form as UseFormReturn<CustomerFormInput>,
    handleContactFieldChange,
    isPending,
    isSubmitDisabled: isPending || !form.formState.isValid || (isEditMode && !form.formState.isDirty),
    onSubmit,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** react-hook-form marks a nested object field's dirty/touched state per leaf key. */
type FieldInteractionFlag = boolean | Record<string, boolean | undefined> | undefined;

/** Returns whether any leaf of a dirty/touched flag (scalar or nested-object) is set. */
function hasInteractionFlag(flag: FieldInteractionFlag): boolean {
  if (typeof flag === 'boolean') return flag;
  if (!flag) return false;
  return Object.values(flag).some(Boolean);
}

/**
 * A field is "pristine" (not yet interacted with) when it's neither dirty (its value differs
 * from the default) nor touched (never blurred). Extracted to keep useCustomerForm's cyclomatic
 * complexity within the ESLint limit.
 */
function isPristine(isDirty: FieldInteractionFlag, isTouched: FieldInteractionFlag): boolean {
  return !hasInteractionFlag(isDirty) && !hasInteractionFlag(isTouched);
}

/** Per-entry dirty/touched leaf flags for the two contact-override fields. */
type ContactInteractionEntry = Partial<Record<ContactField, FieldInteractionFlag>>;

/** Structural subset of RHF's per-entry error node — only the `.message` leaves are read. */
interface ContactErrorEntry {
  contactName?: { message?: string };
  contactPhone?: { message?: string };
}

/** Loosely-typed clearErrors — dynamic indexed field names can't satisfy RHF's literal FieldPath type. */
type ClearErrors = (name: never) => void;

/** Clears one contact-override field's "required" error while it remains untouched. */
function suppressUntouchedContactFieldError(
  clearErrors: ClearErrors,
  index: number,
  field: ContactField,
  hasError: boolean,
  isUntouched: boolean,
): void {
  if (hasError && isUntouched) {
    clearErrors(`addresses.${index}.${field}` as never);
  }
}

/**
 * Clears a single address-book entry's contactName/contactPhone "required" errors while they
 * remain untouched (see the effect above for the full rationale). Extracted to keep that effect's
 * callback under the cyclomatic complexity limit.
 */
function suppressUntouchedContactErrors(
  clearErrors: ClearErrors,
  index: number,
  entryErrors: ContactErrorEntry | undefined,
  dirtyEntry: ContactInteractionEntry | undefined,
  touchedEntry: ContactInteractionEntry | undefined,
): void {
  if (!entryErrors) return;
  suppressUntouchedContactFieldError(
    clearErrors,
    index,
    'contactName',
    !!entryErrors.contactName?.message,
    isPristine(dirtyEntry?.contactName, touchedEntry?.contactName),
  );
  suppressUntouchedContactFieldError(
    clearErrors,
    index,
    'contactPhone',
    !!entryErrors.contactPhone?.message,
    isPristine(dirtyEntry?.contactPhone, touchedEntry?.contactPhone),
  );
}

function buildCreateDefaultValues(): Partial<CustomerFormInput> {
  return {
    addresses: [buildEmptyAddressEntry()],
    companyName: '',
    email: '',
    firstName: '',
    lastName: '',
    loyaltyNumber: '',
    phone: { dialCode: '+33', number: '' },
    type: CUSTOMER_TYPE.CLIENT,
  };
}

function resolvePhoneDefaults(phone: PhoneNumber | null | undefined): PhoneNumber {
  if (!phone) return { dialCode: '+33', number: '' };
  // Handle legacy data where dialCode might be stored as "countryCode"
  const raw = phone as unknown as Record<string, string>;
  const dialCode = phone.dialCode || raw['countryCode'] || '+33';
  return { dialCode, number: phone.number || '' };
}

/**
 * Enforces "exactly one default" across the loaded entries at open time, mirroring the backend's
 * normalizeDefaultAddress: the first flagged entry stays default; when none is flagged
 * (legacy data), the first entry becomes the default.
 */
function normalizeDefaultFlags(entries: AddressBookEntryInput[]): AddressBookEntryInput[] {
  const defaultIndex = entries.findIndex((entry) => entry.isDefault);
  const resolvedDefaultIndex = defaultIndex === -1 ? 0 : defaultIndex;
  return entries.map((entry, index) => ({ ...entry, isDefault: index === resolvedDefaultIndex }));
}

function buildEditDefaultValues(customer: Customer): Partial<CustomerFormInput> {
  // normalizeDefaultFlags is a no-op on an empty array, so a customer with no saved address
  // simply gets addresses: [] — no synthetic draft entry (the empty-state placeholder handles it).
  const addresses = normalizeDefaultFlags(
    customer.addresses.map((address, index) => buildAddressEntryFromCustomerAddress(address, index, customer)),
  );
  return {
    addresses,
    companyName: customer.companyName ?? '',
    email: customer.email ?? '',
    firstName: customer.firstName,
    lastName: customer.lastName,
    loyaltyNumber: customer.loyaltyNumber ?? '',
    phone: resolvePhoneDefaults(customer.phone),
    type: customer.type,
  };
}
